import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNotify } from 'react-admin';
import { apiFetch } from '../lib/apiFetch';
import { DEFAULT_DISMISS_REASON } from './siedList';

/**
 * Row overflow on the SIED page (T-109). "Re-download PDF" logs into SIED,
 * fetches the form's current SP-3 and — when linked — makes it the case's SP-3
 * (older ones archived). For a PDF downloaded before the doctor signed.
 *
 * "Not ours — remove" (T-148) hides an SP-3 shared with us by mistake; the SIED
 * check never touches it again. Only for unlinked rows; undo under Removed.
 */
const DOCTOR_SIGNED = new Set(['certifier_signed_not_validated', 'certifier_signed_validated', 'funeral_rep_signed']);

export const Sp3RowMenu = ({
  formNumber,
  downloadedStatus,
  canRemove = false,
  onDone,
}: {
  formNumber: string;
  /** sied_records.sp3_downloaded_status: SIED status when the held PDF was fetched. */
  downloadedStatus?: string | null;
  /** Offer "Not ours — remove" (unlinked rows only). */
  canRemove?: boolean;
  onDone: () => void;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reason, setReason] = useState(DEFAULT_DISMISS_REASON);
  const [removing, setRemoving] = useState(false);
  const notify = useNotify();

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await apiFetch(`sied/dismiss/${encodeURIComponent(formNumber)}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      notify(`SP-3 ${formNumber} removed`, { type: 'success' });
      setRemoveOpen(false);
      onDone();
    } catch (error: any) {
      const msg = String(error?.message ?? '');
      notify(
        msg.includes('409') ? 'This SP-3 is linked to a case — remove it from the case first' : 'Failed to remove SP-3',
        { type: 'error' },
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleRedownload = async () => {
    setAnchor(null);
    // The doctor-signed PDF is the version we keep; replacing it is a staff call.
    if (
      downloadedStatus &&
      DOCTOR_SIGNED.has(downloadedStatus) &&
      !window.confirm('This replaces the doctor-signed SP-3 on the case — continue?')
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch(`sied/redownload/${encodeURIComponent(formNumber)}`, { method: 'POST' });
      notify(
        res?.funeralId
          ? `SP-3 re-downloaded and filed on case #${res.funeralId}`
          : 'SP-3 re-downloaded (not linked to a case)',
        { type: 'success' },
      );
      onDone();
    } catch (error: any) {
      const msg = String(error?.message ?? '');
      notify(
        msg.includes('409')
          ? 'A SIED check is running — try again in a minute'
          : msg.includes('404')
            ? 'SP-3 not found on SIED'
            : `Re-download failed: ${msg || 'unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setBusy(false);
    }
  };

  if (busy) return <CircularProgress size={18} sx={{ ml: 1 }} aria-label="Re-downloading SP-3" />;
  return (
    <>
      <IconButton size="small" aria-label="More actions" title="More actions" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => void handleRedownload()}>Re-download PDF</MenuItem>
        {canRemove && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setReason(DEFAULT_DISMISS_REASON);
              setRemoveOpen(true);
            }}
          >
            Not ours — remove
          </MenuItem>
        )}
      </Menu>
      <Dialog open={removeOpen} onClose={() => !removing && setRemoveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove SP-3 {formNumber}?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" mb={2}>
            It disappears from the list and SIED checks stop updating it. You can restore it under Removed.
          </DialogContentText>
          <TextField fullWidth size="small" label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)} disabled={removing}>
            Cancel
          </Button>
          <Button variant="contained" color="error" disabled={removing} onClick={() => void handleRemove()}>
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
